/*
  KC AR - launches the platform's own AR viewer at true scale.

  PORTED from madtag's assets/mt-ar.js. See snippets/kc-ar.liquid for what was
  dropped and why. In short: madtag renders two layouts and a third of that file
  exists to keep them from both being visible; KingCave renders one chip, so the
  `wide` branch, the `hasChip` dedupe and the document-level fallbacks are gone.
  Everything else is the same code and the same rulings.

  window.kcInitAR(root) is idempotent (guards on el.__kcAR) so a future in-place
  rerender can re-run it without double-binding.

  iOS does not touch this file. The markup ships a real `<a rel="ar">` pointing
  at our vertically-anchored .usdz and Safari handles it natively. An earlier
  version over there synthesised a hidden anchor and called .click() on it -
  that silently does nothing, because a display:none element cannot be
  activated. If AR ever stops launching on iPhone, check that the anchor is
  rendered, still has its <img> child, and has no `download` attribute, before
  suspecting the model.

  Deliberately no <model-viewer>: iOS has no immersive-ar WebXR, so Quick Look
  is the only route; model-viewer would cost ~290KB gzipped to do the same
  thing, and its ar-placement="wall" never reaches iOS anyway.

  ANDROID IS DELIBERATELY NOT OFFERED AR - see the isAndroid() comment. Do not
  "restore" the Scene Viewer path without reading it first.

  THE ANCHOR EXISTS EVEN WHEN THE ARRIVAL VARIANT HAS NO MODEL. kc-ar.liquid
  renders the entry point whenever ANY variant of the product carries models,
  with no `href` and `hidden` set when the CURRENT one does not. So "the node is
  in the DOM" is not "this variant is launchable": `hasModel` below is what
  decides whether the anchor is revealed, and the swap listener is what moves
  the href in later. Never reveal an anchor with no href.
*/
(function () {
  'use strict';

  var CFG = window.KC_AR_CFG || {};

  /* ⛔ ERROR TELEMETRY IS OFF BY DEFAULT ON KINGCAVE, and that is a decision,
     not an omission. madtag posts asset failures to an insert-only `ar_events`
     table in its own Supabase with a publishable key - the security boundary
     there is RLS, not secrecy. KingCave has its own Supabase (`kc store`) and
     no such table, and pointing this at madtag's project would file KingCave's
     failures in another store's database.

     To switch it on: create the table (madtag's DDL is
     docs/ar-events-table.sql over there), give anon an INSERT policy and NO
     SELECT policy, then set window.KC_AR_CFG = { errorEndpoint, errorKey }
     before this script loads. The publishable key is the only key that may
     appear here; the service role key must never be in this repo.

     Behavioural events are unaffected - they go to Shopify.analytics.publish,
     which is already on every storefront page and costs no bytes. That bus is
     WRITE-ONLY though: nothing published there can be queried back, which is
     exactly why the two events that mean something is BROKEN want a database. */
  var ERR_URL = CFG.errorEndpoint || '';
  var ERR_KEY = CFG.errorKey || '';
  var TIP_KEY = 'kc-ar-tip-seen';

  /* ---------------------------------------------------------------------
     Instrumentation.

     Neither platform tells the page whether the model rendered or whether a
     plane was found - that is OS sandboxing, not a missing API. So everything
     here is a PROXY, and the taxonomy is built around the one question we can
     actually answer: did the launch happen, and did the asset exist. This
     feature shipped broken twice on madtag and both times a human found it
     first.
     --------------------------------------------------------------------- */
  function track(name, data) {
    data = data || {};
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('kingcave:' + name, data);
      }
    } catch (e) { /* telemetry must never break the page */ }
  }

  var errSeen = {};
  function reportError(kind, detail, extra) {
    if (!ERR_URL || !ERR_KEY) return;
    var key = kind + '|' + detail;
    if (errSeen[key]) return;   // one row per distinct failure per page view
    errSeen[key] = 1;
    extra = extra || {};
    try {
      var body = JSON.stringify({
        feature: 'ar_wall',
        kind: String(kind).slice(0, 64),
        detail: String(detail).slice(0, 400),
        href: location.href.split('?')[0].slice(0, 400),
        ua: String(navigator.userAgent || '').slice(0, 400),
        device_class: extra.device_class || null,
        product_id: extra.product_id || null
      });
      /* NOT sendBeacon: it cannot set the apikey header Supabase requires, and
         moving the key into the query string would put it in every access log
         between here and there. Every call site fires while the page is still
         alive, so keepalive+fetch loses nothing. */
      if (window.fetch) {
        fetch(ERR_URL, {
          method: 'POST',
          keepalive: true,
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'apikey': ERR_KEY,
            'Authorization': 'Bearer ' + ERR_KEY,
            'Prefer': 'return=minimal'
          },
          body: body
        })['catch'](function () { /* telemetry must never break the page */ });
      }
    } catch (e) { /* swallow */ }
  }

  /* Quick Look, including the non-AR object view on pre-A9 hardware. That
     over-reporting is fine: it degrades to a 3D preview, never a dead tap.

     relList.supports('ar') is the documented probe but it is NOT sufficient on
     its own - it has been observed returning false on real iOS, which sends an
     iPhone down the desktop branch and kills the feature for the one platform
     that needs it. So UA is a second opinion, never a veto: any iOS device is
     treated as capable. iPadOS 13+ reports as "Macintosh", hence maxTouchPoints. */
  function supportsQuickLook() {
    var ua = navigator.userAgent || '';
    try {
      var a = document.createElement('a');
      if (a.relList && a.relList.supports && a.relList.supports('ar')) return true;
    } catch (e) { /* relList.supports throws on very old browsers */ }
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  }

  /* Android gets NO AR button, deliberately (madtag, 2026-08-01).
     Do not restore the Scene Viewer intent - it is in madtag's git history at
     4e88f29 - and do not restore it at all until BOTH of the following are true:

       1. google-ar/arcore-android-sdk#1752 is closed. Since the Oct-2025 Play
          Services update, Scene Viewer's AR mode crashes on launch on Android
          16 - on Google's OWN sample models, not just ours.
       2. Vertical placement actually works. `enable_vertical_placement` is
          documented only for the googlequicksearchbox package and has a
          years-long history of being ignored in scene-viewer mode
          (google/model-viewer#2303, #2154). A wall-art button that drops the
          plate on the FLOOR is worse than no button.

     A button labelled "See it on your wall" that crashes or lies costs more
     trust than an absent one. We count the loss instead - see ar_unavailable -
     so the decision to re-enable can be made on a number.
     Re-enabling requires a real Android device test, not a doc read. */
  function isAndroid() {
    return /Android/.test(navigator.userAgent);
  }

  /* The hint under the chip. It teaches the one thing that actually decides
     whether ARKit finds the wall - distance and light - and then gets out of
     the way for good: dismissed by hand, or automatically once the visitor has
     been into AR once and no longer needs telling. */
  function tipSeen() {
    try { return window.localStorage.getItem(TIP_KEY) === '1'; }
    catch (e) { return false; }   // Safari private mode throws on access
  }
  function markTipSeen() {
    try { window.localStorage.setItem(TIP_KEY, '1'); } catch (e) { /* fine */ }
  }

  /* Desktop has no AR at all - AR Quick Look is iOS/iPadOS only and there is no
     handheld WebXR in any desktop browser. A sentence telling someone to "open
     this on your phone" is a dead end; a QR of this exact page is one scan.
     The encoder is only fetched once a desktop visitor actually asks. */
  var QR_LOADED = null;
  function loadQr(src) {
    if (QR_LOADED) return QR_LOADED;
    QR_LOADED = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return QR_LOADED;
  }

  /* The QR popover hangs off the 40x40 chip, and the chip is also the phone's
     launch button - so the popover must never open on a phone. One width test,
     used by both the toggle and the close path, matching the 1181px boundary
     the CSS uses. */
  function qrWidth() {
    return !window.matchMedia || window.matchMedia('(min-width:1181px)').matches;
  }

  function hideHandoff(note, link) {
    note.hidden = true;
    if (link) link.setAttribute('aria-expanded', 'false');
  }

  function showHandoff(el, note, link) {
    note.hidden = false;
    if (link) link.setAttribute('aria-expanded', 'true');
    var src = window.KC_QR_SRC;
    var slot = el.querySelector('[data-kc-ar-qr]');
    if (!src || !slot || slot.getAttribute('data-done')) return;
    loadQr(src)
      .then(function () {
        if (!window.qrcode) return;
        var q = window.qrcode(0, 'M');
        /* This page, unless the instance names a different destination. On the
           PDP the two are the same thing and no attribute is set. */
        q.addData(el.getAttribute('data-qr-href') || window.location.href.split('#')[0]);
        q.make();
        slot.innerHTML = q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
        slot.setAttribute('data-done', '1');
      })
      .catch(function () { /* the note alone still says what to do */ });
  }

  /* ⛔ THERE IS NO FAILURE DETECTOR, AND ONE MUST NOT BE PUT BACK.

     madtag had a `showFailure()` that ran 2500 ms after the tap whenever the
     page had not gone hidden, on the theory that Quick Look suspends the page.
     It does not. iOS presents its own consent alert first ("View in AR?",
     WebKit SystemPreviewControllerCocoa.mm:486) and then presents Quick Look as
     a MODAL over the web view rather than backgrounding Safari, so the page
     stays visible right through a perfectly normal launch and the timer fired
     anyway.

     Its message was worse than its trigger: the QR branch was gated on
     qrWidth(), so below 1181px the one reachable branch printed "AR needs a
     real iPhone or Android phone" to somebody holding an iPhone, and nothing
     ever removed the paragraph again.

     Every row it wrote in three weeks was a false positive. Neither platform
     tells the page whether AR launched or whether a plane was found, so there
     is no honest proxy to replace it with. Do not write a better timer.
     `ar_click` and `ar_returned` are what we have. */

  function initAR(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-kc-ar]');
    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        if (el.__kcAR) return;
        el.__kcAR = true;

        var link = el.querySelector('[data-kc-ar-launch]');
        if (!link) return;
        var note = el.querySelector('[data-kc-ar-note]');

        var quickLook = supportsQuickLook();
        var android = isAndroid();
        var canAR = quickLook;   // Android is excluded on purpose - see isAndroid()
        var deviceClass = quickLook ? 'ios_quicklook' : android ? 'android' : 'desktop';
        var productId = el.getAttribute('data-product-id') || null;

        /* Does the variant the page ARRIVED on have a model? The markup renders
           whenever ANY variant of the product does, so "the node exists" is not
           "this variant is launchable". Nothing below may reveal the anchor
           unless this is true - the server deliberately shipped it with no
           href, and an href-less anchor that became visible would be a button
           that does nothing. */
        var hasModel = !!el.getAttribute('data-usdz') && !!el.getAttribute('data-glb');

        /* Android still gets no LAUNCH: no button, and NOT the desktop QR
           handoff either - "scan this to open it on your phone" is nonsense
           when you already are on the phone. Counted, not silent, so the size
           of what we are giving up is a number rather than a guess. The
           absence is EXPLAINED rather than silent: a line of copy that is
           already in the markup gets unhidden. */
        if (android) {
          var aNote = el.querySelector('[data-kc-ar-android]');
          if (aNote) aNote.hidden = false;
          track('ar_unavailable', {
            device_class: 'android',
            reason: 'scene_viewer_vertical_broken',
            product_id: productId
          });
          return;
        }

        /* ── the size swap ───────────────────────────────────────────────────
           A metal poster's Size option IS the object on the wall: 810 x 380 and
           600 x 300 are the same artwork and different geometry. kc-plate.js
           swaps the variant in place without a page load and names the new one;
           this repoints the anchor at that variant's own model.

           Only the href moves. The anchor itself is never rebuilt, because a
           freshly created <a rel="ar"> outside a user gesture is exactly the
           thing that silently does nothing on iOS - and the href is read at
           click time, so setting it here keeps the launch synchronous in the
           handler.

           Registered ABOVE the capability returns below, and gated on
           `activated` instead, so a desktop browser narrowed under 1181px does
           not leave a stale anchor behind if a resize ever makes it visible.

           `kcp:variant` does NOT bubble, so this listens on the plate root that
           kc-plate.js dispatches on, not on document. */
        var activated = false;
        var plateRoot = (el.closest && el.closest('[data-kc-plate]')) ||
          document.querySelector('[data-kc-plate]');
        if (plateRoot) {
          plateRoot.addEventListener('kcp:variant', function (ev) {
            var ar = ev.detail && ev.detail.ar;
            /* Facts about the NEW variant either way - a model-less variant is
               still the variant the page is on, and leaving these behind is the
               same staleness in a quieter attribute. */
            el.setAttribute('data-variant-id', ev.detail.id);
            var fb = el.getAttribute('data-fallback');
            var newFallback = fb ? fb.split('?')[0] + '?variant=' + ev.detail.id : null;
            if (newFallback) el.setAttribute('data-fallback', newFallback);

            if (!ar || !ar.usdz || !ar.glb) {
              /* This variant has no model. Hide the button AND drop the href
                 rather than leave the previous variant's link in place: a stale
                 link launches the wrong SIZE, which is the one thing AR exists
                 to get right. `hidden` alone is not enough - an author display
                 rule beats the attribute, and a still-tappable anchor pointing
                 at another size's geometry is the exact failure being fixed.
                 removeAttribute, never href='': a blank href is still a link. */
              link.hidden = true;
              link.removeAttribute('href');
              el.setAttribute('data-glb', '');
              el.setAttribute('data-usdz', '');
              return;
            }
            el.setAttribute('data-glb', ar.glb);
            el.setAttribute('data-usdz', ar.usdz);
            /* ⛔ canonicalWebPageURL - same fix as kc-ar.liquid's initial
               render (madtag-theme's 2026-08-20 AR Quick Look audit §4c): a
               share from inside Quick Look must carry this product page, not
               the raw storage URL the .usdz lives at. newFallback is this
               same anchor's own data-fallback, freshly rewritten above for
               the variant just swapped to. */
            link.href = ar.usdz + '#allowsContentScaling=0' +
              (newFallback ? '&canonicalWebPageURL=' + encodeURIComponent(newFallback) : '');
            if (ar.label) {
              link.setAttribute(
                'aria-label',
                'View ' + (el.getAttribute('data-title') || 'this piece') +
                  ' at ' + ar.label + ' on your wall at true size'
              );
            }
            if (activated) link.hidden = false;
          });
        }

        /* ⚠️ `note && qrWidth()`, not `note`. The chip carries the popover at
           every width but only opens it above 1180px, so a plain `note` test
           would show the chip to a DESKTOP browser narrowed under 1181 - where
           it can neither launch AR nor open a QR. A button that does nothing. */
        if (!canAR && !(note && qrWidth())) return;

        /* `activated` means "this instance owns the visible entry point", and
           it is set even when the arrival variant has no model - otherwise the
           swap listener above could never reveal the anchor after the buyer
           picks a size that does have one, which is the whole reason the markup
           ships. Only the REVEAL is gated on the model, never the ownership. */
        activated = true;
        if (hasModel) link.hidden = false;

        /* An impression is a button a visitor could see. Firing it for a
           variant whose anchor is still hidden would inflate the denominator of
           every funnel below it. */
        if (hasModel) track('ar_impression', { device_class: deviceClass, product_id: productId });

        var tip = el.querySelector('[data-kc-ar-tip]');
        function hideTip(remember) {
          if (!tip || tip.hidden) return;
          tip.hidden = true;
          if (remember) markTipSeen();
        }
        if (tip) {
          if (canAR && hasModel && !tipSeen()) tip.hidden = false;
          var tipX = tip.querySelector('[data-kc-ar-tip-dismiss]');
          if (tipX) {
            tipX.addEventListener('click', function () { hideTip(true); });
          }
        }

        /* The only failure this feature can see by itself. A 404 or a broken
           pipeline is otherwise completely silent - the model just never
           appears. */
        if (window.fetch) {
          var usdz = el.getAttribute('data-usdz');
          if (usdz) {
            fetch(usdz, { method: 'HEAD' })
              .then(function (r) {
                if (!r.ok) {
                  track('ar_asset_error', { device_class: deviceClass, status: r.status, product_id: productId });
                  reportError('asset_http', r.status + ' ' + usdz, { device_class: deviceClass, product_id: productId });
                }
              })
              .catch(function () {
                track('ar_asset_error', { device_class: deviceClass, status: 0, product_id: productId });
                reportError('asset_network', usdz, { device_class: deviceClass, product_id: productId });
              });
          }
        }

        var clickedAt = 0;
        var wentAway = false;

        link.addEventListener('click', function (e) {
          clickedAt = Date.now();
          wentAway = false;
          track('ar_click', { device_class: deviceClass, product_id: productId });

          /* iOS: do NOT preventDefault - the anchor IS the launch mechanism,
             and nothing is checked afterwards. See the ⛔ block above initAR()
             for why the launch is no longer second-guessed. */
          if (quickLook) return;
          e.preventDefault();
          /* Desktop: the chip TOGGLES its QR popover. A popover with no way
             back is a trap on a 40px chip - it would sit over the plate until
             the page reloaded. Guarded on width because this chip is also the
             phone's launch button. */
          if (note && qrWidth()) {
            if (note.hidden) showHandoff(el, note, link);
            else hideHandoff(note, link);
          }
        });

        /* ── closing the popover ────────────────────────────────────────────
           Three ways out, all of them the ones a user already expects: the ×,
           Escape, and a click anywhere else. Bound once per instance and only
           where a popover exists, so a phone never pays for any of it. */
        if (note) {
          /* Only claim expandability where the chip actually expands. On a
             phone this same anchor launches Quick Look and controls nothing. */
          if (qrWidth()) link.setAttribute('aria-expanded', 'false');
          var closeBtn = note.querySelector('[data-kc-ar-note-dismiss]');
          if (closeBtn) {
            closeBtn.addEventListener('click', function () {
              hideHandoff(note, link);
              link.focus();
            });
          }
          document.addEventListener('keydown', function (e) {
            if (note.hidden) return;
            if (e.key === 'Escape' || e.key === 'Esc') {
              hideHandoff(note, link);
              link.focus();
            }
          });
          document.addEventListener('click', function (e) {
            if (note.hidden) return;
            if (note.contains(e.target) || link.contains(e.target)) return;
            hideHandoff(note, link);
          });
        }

        /* Returning from the AR viewer is the closest thing to a success signal
           either platform gives us: iOS restores the page from bfcache. */
        document.addEventListener('visibilitychange', function () {
          if (!clickedAt) return;
          if (document.visibilityState === 'hidden') wentAway = true;
          else if (wentAway) {
            track('ar_returned', { device_class: deviceClass, dwell_ms: Date.now() - clickedAt, product_id: productId });
            clickedAt = 0;
            /* They have now seen AR for themselves; the instructions have done
               their job and should not greet them again. */
            hideTip(true);
          }
        });
        window.addEventListener('pageshow', function (ev) {
          if (ev.persisted && clickedAt) {
            wentAway = true;
            track('ar_returned', { device_class: deviceClass, dwell_ms: Date.now() - clickedAt, via: 'bfcache', product_id: productId });
            clickedAt = 0;
            hideTip(true);
          }
        });

      })(nodes[i]);
    }
  }

  window.kcInitAR = initAR;

  function boot() {
    initAR(document);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
